import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';
import type { WeekPeriod } from './timesheet-api';

export interface GridDay {
  date: string;
  locked: boolean;
  isToday: boolean;
  isWeekend: boolean;
  totalSeconds: number;
}

export interface GridRow {
  projectId: string;
  activityId: string;
  project: { id: string; name: string; customer: { name: string } };
  activity: { id: string; name: string };
  description: string | null;
  /** Seconds per date. A missing date means nothing was booked that day. */
  cells: Record<string, number>;
  /** Pinned: this row is on every week, booked to or not. */
  favorite: boolean;
  /**
   * Days on this row whose entries carry something a cell cannot show - a tag, a
   * rate override, or a note of their own. The grid marks them and links to the
   * day list rather than letting the field vanish.
   */
  detailDays: string[];
  totalSeconds: number;
}

/** A pairing this person books often, offered as a one-click row. */
export interface GridSuggestion {
  projectId: string;
  activityId: string;
  project: { id: string; name: string; customer: { name: string } };
  activity: { id: string; name: string };
}

export interface WeekGrid {
  weekStart: string;
  weekEnd: string;
  /** Submission state per part - two parts on a week crossing a month end. */
  periods: WeekPeriod[];
  /** Somebody else's week. Every day comes back locked and nothing may be typed. */
  readOnly: boolean;
  employee: { id: string; displayName: string };
  days: GridDay[];
  rows: GridRow[];
  /** Excludes anything already on the grid, so nothing is offered twice. */
  suggestions: GridSuggestion[];
  /** How many rows one person may pin. */
  favoriteLimit: number;
  totalSeconds: number;
}

export interface GridSaveResult {
  created: number;
  updated: number;
  deleted: number;
  /** Closed DAYS a change was attempted on, not cells - see GridService.save. */
  skippedLocked: number;
}

export const useWeekGrid = (date: string, employeeId?: string, enabled = true) =>
  useQuery({
    queryKey: ['timesheet', 'grid', date, employeeId ?? 'me'],
    queryFn: () =>
      api.get<WeekGrid>(
        `/timesheet/grid?date=${date}${employeeId ? `&employeeId=${employeeId}` : ''}`,
      ),
    enabled,
  });

export const useSaveGrid = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      date: string;
      rows: { projectId: string; activityId: string; description?: string | null; cells: Record<string, number> }[];
    }) => api.post<GridSaveResult>('/timesheet/grid', payload),
    onSuccess: () => {
      // The grid, the day list and the month view are three views of the same
      // entries, so all of them go stale together.
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
      void queryClient.invalidateQueries({ queryKey: ['working-time'] });
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
};

/**
 * Pin or unpin a row.
 *
 * Invalidates the grid rather than patching the row in place: pinning ADDS a row
 * to every other week too, and unpinning removes an empty one from this one, so
 * the server's answer is the only honest picture.
 */
export const useSetFavorite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { projectId: string; activityId: string; favorite: boolean }) =>
      api.post<{ favorite: boolean }>('/timesheet/grid/favorites', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
};
